import React, { useState, useEffect, useCallback } from 'react';
import PropTypes from 'prop-types';
import { LoaderDefault } from '@vr/lib';
import styled, { css } from 'styled-components';
import { GoogleMapsWithoutScript } from '../components/GoogleMaps';
import Box from '../components/Box';
import MapContainer from '../elements/MapContainer';
import MapaContext from '../context/Mapa';
import Search from './Search';
import Results from './Results';

const MainContainer = styled.div`
  ${({ open }) =>
    open &&
    css`
      overflow: hidden;
    `}
`;

function Mapa({
  findEstablishment,
  locations,
  products,
  open,
  renderOption,
  searchTitle,
  isLoading,
  showResumoResultados,
  googleMapsConfig,
  defaultValues,
  height,
  companyName,
  ...rest
}) {
  const [coordenadas, setCoordenadas] = useState({ lat: -13.33, lng: -53.2 });
  const [zoom, setZoom] = useState(4);
  const [openBox, setOpenBox] = useState(open);
  const [fullBox, setFullBox] = useState(false);
  const [raio, setRaio] = useState(1);
  const [showResultados, setShowResultados] = useState(false);
  const [search, setSearch] = useState(defaultValues);
  const [empresaSelecionada, setEmpresaSelecionada] = useState({});

  const getZoomByRaio = useCallback(() => {
    if (raio === 3) {
      return 14;
    }

    if (raio === 5) {
      return 13;
    }

    return 16;
  }, [raio]);

  function onChangeCoordenadas(coord) {
    if (coord.lat && coord.lng) {
      setCoordenadas(coord);
      setZoom(getZoomByRaio());
    }
  }

  function renderContent() {
    if (isLoading) {
      return <LoaderDefault />;
    }

    if (showResultados) {
      return <Results />;
    }

    return (
      <Search
        products={products}
        renderOption={renderOption}
        onSelectPlace={onChangeCoordenadas}
        searchTitle={searchTitle}
        defaultValues={search}
        height={height}
      />
    );
  }

  useEffect(() => {
    setOpenBox(open);
  }, [open]);

  useEffect(() => {
    if (Object.values(search).length) {
      setZoom(getZoomByRaio());
    }
  }, [raio, search, getZoomByRaio]);

  const contextValue = {
    locations,
    openBox,
    setOpenBox,
    raio,
    setRaio,
    setShowResultados,
    showResumoResultados,
    search,
    setSearch,
    findEstablishment,
    empresaSelecionada,
    setEmpresaSelecionada,
    fullBox,
    setFullBox,
    companyName,
  };

  return (
    <MainContainer id="mapa-main-container" open={openBox}>
      <MapaContext.Provider value={contextValue}>
        <MapContainer {...rest}>
          <GoogleMapsWithoutScript
            center={coordenadas}
            zoom={zoom}
            config={googleMapsConfig}
            height={height}
            google={window.google}
          />
          <Box>
            <div>{renderContent()}</div>
          </Box>
        </MapContainer>
      </MapaContext.Provider>
    </MainContainer>
  );
}

Mapa.propTypes = {
  /**
   * Essa propriedade deve receber uma função que será executada quando o usuário clicar na opção de buscar.
   */
  findEstablishment: PropTypes.func.isRequired,
  /**
   * Esta propriedade recebe um array de objetos contendo os estabelecimentos que deverão ser exibidos no mapa.
   */
  locations: PropTypes.arrayOf(
    PropTypes.shape({
      latitude: PropTypes.string.isRequired,
      longitude: PropTypes.string.isRequired,
    }),
  ).isRequired,
  /**
   * Esta propriedade receberá um array com os códigos dos produtos disponíveis para seleção
   */
  products: PropTypes.arrayOf(PropTypes.any).isRequired,
  /**
   * Esta propriedade define se a caixa de pesquisa será carregada aberta ou fechada
   */
  open: PropTypes.bool,
  /**
   * Função para customizar o conteúdo que será exibido na caixa de seleção de produtos
   */
  renderOption: PropTypes.func,
  /**
   * DEPRECATED: Título a ser exibido na caixa de busca
   */
  searchTitle: PropTypes.string,
  /**
   * Indicação de loading. Será apresentado o LoaderDefault da @vr/lib
   */
  isLoading: PropTypes.bool,
  /**
   * Irá exibir a página com o resumo de resultados
   */
  showResumoResultados: PropTypes.bool,
  /**
   * Configuração adicional do Google Maps
   */
  googleMapsConfig: PropTypes.shape({}),
  /**
   * Valores default a serem inseridos no formulário
   */
  defaultValues: PropTypes.shape({}),
  /**
   * Define a altura do mapa
   */
  height: PropTypes.string,
  /**
   * Define se o mapa deve carregar o script do google maps por padrão ou não.
   * Desabilitando essa opção o usuário deve realizar a inserção do script do Google Maps manualmente na página
   */
  loadGooogleMapsScript: PropTypes.bool,
  /**
   * Prefixo do nome da empresa a ser exibido nos nomes de produtos
   * default: vazio
   */
  companyName: PropTypes.string,
};

Mapa.defaultProps = {
  open: false,
  renderOption: null,
  searchTitle: null,
  isLoading: false,
  showResumoResultados: false,
  googleMapsConfig: {},
  defaultValues: {},
  height: '100vh',
  loadGooogleMapsScript: true,
  companyName: '',
};

export default Mapa;
